import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProductOffer from "./ProductOffer";
import { setDocumentTitle } from "../../utils/seo";


const DEFAULT_IMAGES = [
  "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=800&q=80", // generic product
  "https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?auto=format&fit=crop&w=800&q=80", // gadget flat lay
  "https://images.unsplash.com/photo-1503602642458-232111445657?auto=format&fit=crop&w=800&q=80", // minimal product
  "https://images.unsplash.com/photo-1495433324511-bf8e92934d90?auto=format&fit=crop&w=800&q=80", // neutral product
];



export default function ProductDetails( {isLoggedIn} ) {
  const { hyveProductId } = useParams();
//   console.log(hyveProductId);

  const [product, setProduct] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const THUMBS_PER_PAGE = 7;
  const [thumbStartIndex, setThumbStartIndex] = useState(0);


  useEffect(() => {
    if (!hyveProductId) return;

    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await api.get(
          `/master-catalogue/api/catalogue/product/${hyveProductId}`,
          {
            headers: { Accept: "application/json" },
          }
        );
        setProduct(res.data);
      } catch (err) {
        setError("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [hyveProductId]);


  // Set document title based on product name
  useEffect(() => {
  if (product?.productName) {
    setDocumentTitle(product.productName);
  }
}, [product]);


  if (loading) return <p className="p-4">Loading product…</p>;
  if (error) return <p className="p-4 text-red-600">{error}</p>;
  if (!product) return null;

  const images = product.media?.filter((m) => m.type === "IMAGE") || [];
  const finalImages =
  images.length > 0
    ? images.map((img) => img.url)
    : DEFAULT_IMAGES;


  return (
    <div className="max-w-6xl mx-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* ---------------- IMAGE SECTION ---------------- */}
       {/* <div>
        <div className="border rounded-xl p-2">

  <img
    src={finalImages[activeImageIndex]}
    alt={product.productName}
    className="w-full h-96 object-contain "
  />
        </div>


  {finalImages.length > 1 && (
    <div className="flex gap-2 mt-4 overflow-x-auto">
      {finalImages.map((img, index) => (
        <button
          key={index}
          onClick={() => setActiveImageIndex(index)}
          className={`border rounded-lg p-1 ${
            activeImageIndex === index
              ? "border-indigo-600"
              : "border-gray-300"
          }`}
        >
          <img
            src={img}
            alt={`thumb-${index}`}
            className="w-16 h-16 object-contain"
          />
        </button>
      ))}
    </div>
  )}
       </div> */}
       <div>
  {/* -------- MAIN IMAGE -------- */}
  <div className="border rounded-xl p-2">
    <img
      src={finalImages[activeImageIndex]}
      alt={product.productName}
      className="w-full h-96 object-contain"
    />
  </div>

  {/* -------- THUMBNAIL CAROUSEL -------- */}
  {finalImages.length > 1 && (
    <div className="flex items-center gap-2 mt-4">
      {/* LEFT ARROW */}
     {finalImages.length > 7 && (<button
        onClick={() =>
          setThumbStartIndex((prev) => Math.max(prev - 1, 0))
        }
        disabled={thumbStartIndex === 0}
        className="p-2 border rounded-lg disabled:opacity-40"
      >
        <ChevronLeft size={22} />
      </button>)
    }

      {/* THUMBNAILS */}
      <div className="flex gap-2 overflow-hidden">
        {finalImages
          .slice(
            thumbStartIndex,
            thumbStartIndex + THUMBS_PER_PAGE
          )
          .map((img, index) => {
            const actualIndex = thumbStartIndex + index;

            return (
              <button
                key={actualIndex}
                onClick={() => setActiveImageIndex(actualIndex)}
                className={`border rounded-lg p-1 ${
                  activeImageIndex === actualIndex
                    ? "border-indigo-600"
                    : "border-gray-300"
                }`}
              >
                <img
                  src={img}
                  alt={`thumb-${actualIndex}`}
                  className="w-16 h-16 object-contain"
                />
              </button>
            );
          })}
      </div>

      {/* RIGHT ARROW */}
       {finalImages.length > 7 &&(<button
        onClick={() =>
          setThumbStartIndex((prev) =>
            Math.min(
              prev + 1,
              finalImages.length - THUMBS_PER_PAGE
            )
          )
        }
        disabled={
          thumbStartIndex + THUMBS_PER_PAGE >= finalImages.length
        }
        className="p-2 border rounded-lg text-xl disabled:opacity-40"
      >
        <ChevronRight size={22} />
      </button> )}
    </div>
  )}
       </div>



      {/* ---------------- PRODUCT INFO ---------------- */}
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">{product.productName}</h1>

        <ProductOffer
        isLoggedIn={isLoggedIn}
        />
        {/* <h1 className="text-2xl font-semibold">{product.productName}</h1> */}

        {product.brand?.brandName && (
          <p className="text-sm text-gray-600">
            <b>Brand:</b> {product.brand.brandName}
          </p>
        )}

        {product.categoryPath && (
          <p className="text-sm text-gray-600">
            <b>Category:</b> {product.categoryPath}
          </p>
        )}

        {product.status && (
          <p className="text-sm text-gray-600">
            <b>Status:</b> {product.status}
          </p>
        )}

        {product.modelNumber && (
          <p className="text-sm text-gray-600">
            <b>Model Number:</b> {product.modelNumber}
          </p>
        )}

        {product.productType && (
          <p className="text-sm text-gray-600">
            <b>Product Type:</b> {product.productType}
          </p>
        )}

        {/* {product.hyveProductId && (
          <p className="text-sm text-gray-600">
            <b>Hyve Product ID:</b> {product.hyveProductId}
          </p>
        )} */}

        {/* ---------------- DESCRIPTION ---------------- */}
        {product.description?.trim() && (
          <div>
            <h2 className="font-semibold mb-1">Description</h2>

            <p
              className={`text-sm text-gray-700 whitespace-pre-line ${
                !showFullDescription ? "line-clamp-2" : ""
              }`}
            >
              {product.description}
            </p>

            <button
              onClick={() =>
                setShowFullDescription((prev) => !prev)
              }
              className="mt-1 text-sm text-indigo-600 hover:underline"
            >
              {showFullDescription ? "View less" : "View more"}
            </button>
          </div>
        )}

        {/* ---------------- SPECIFICATIONS ---------------- */}
        {product.attributes?.length > 0 && (
          <div>
            <h2 className="font-semibold mb-1">Specifications</h2>
            <ul className="text-sm text-gray-700 list-disc pl-5">
              {product.attributes.map((attr, index) => (
                <li key={index}>
                  <b>{attr.code.replace(/_/g, " ")}:</b>{" "}
                  {attr.value}
                  {attr.unit && ` ${attr.unit}`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ---------------- VARIANTS ---------------- */}
        {product.variants?.length > 0 && (
          <div>
            {/* <h2 className="font-semibold mb-1">Variants</h2> */}

            {product.variants.map((variant, index) => (
              <div
                key={index}
                className="border rounded-lg p-3 mb-2 text-sm"
              >
                {/* {variant.sku && (
                  <p>
                    <b>SKU:</b> {variant.sku}
                  </p>
                )} */}

                {/* {variant.barcode && (
                  <p>
                    <b>Barcode:</b> {variant.barcode}
                  </p>
                )} */}

                {variant.attributes?.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {variant.attributes.map((attr, i) => (
                      <p key={i}>
                        <b>
                          {attr.code.replace(/_/g, " ")}:
                        </b>{" "}
                        {attr.value}
                        {attr.unit && ` ${attr.unit}`}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
